import { c as _c } from "react/compiler-runtime";
/**
 * @author Team CMS
 *
 * Hook to add video JsonLD schema to the page.
 */
import { useEffect } from 'react';
/**
 * Format duration in milliseconds to ISO 8601 duration string.
 *
 * @see https://en.wikipedia.org/wiki/ISO_8601#Durations
 * @param milliseconds Video duration in milliseconds
 * @returns ISO 8601 formatted duration string
 * @throws {Error} If milliseconds is negative
 * @example
 * ```ts
 * formatDuration(65000) // Returns 'PT1M5S'
 * formatDuration(3600000) // Returns 'PT60M0S'
 * formatDuration(0) // Returns 'PT0M0S'
 * ```
 */
function formatDuration(milliseconds) {
  if (milliseconds < 0) {
    throw new Error('Duration cannot be negative');
  }
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `PT${minutes}M${remainingSeconds}S`;
}
/**
 * Hook to add video JsonLD schema to the page.
 *
 * @param param0 - Video schema properties including title, src, thumbnail, uploadDate, and duration.
 * @param param0.title
 * @param param0.src
 * @param param0.thumbnail
 * @param param0.uploadDate
 * @param param0.duration
 */
export function useVideoJsonLD(t0) {
  const $ = _c(7);
  const {
    title,
    src,
    thumbnail,
    uploadDate,
    duration
  } = t0;
  let t1;
  let t2;
  if ($[0] !== duration || $[1] !== src || $[2] !== thumbnail || $[3] !== title || $[4] !== uploadDate) {
    t1 = () => {
      if (typeof window === "undefined") {
        return function noop() {};
      }
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: title,
        embedUrl: src,
        thumbnailUrl: thumbnail,
        uploadDate: uploadDate || new Date().toISOString(),
        duration: duration && formatDuration(duration)
      });
      document.body.append(script);
      return function cleanUp() {
        script.remove();
      };
    };
    t2 = [title, src, thumbnail, uploadDate, duration];
    $[0] = duration;
    $[1] = src;
    $[2] = thumbnail;
    $[3] = title;
    $[4] = uploadDate;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
}